<template>
    <div
      v-if="showBlock"
      v-show="timerBarVisible"
      class="session-expiry-indicator top-nav-session"
    :class="{
      'session-expiry-indicator--warn': isWarning,
      'session-expiry-indicator--critical': isCritical,
      'session-expiry-indicator--expired': isExpired
    }"
    role="status"
    :title="tooltipText"
  >
    <div class="session-expiry-indicator__icon" aria-hidden="true">
      <i class="bi bi-clock-history" />
    </div>
    <div class="session-expiry-indicator__main">
      <template v-if="tokenError">
        <p class="session-expiry-indicator__title">Session unavailable</p>
        <p class="session-expiry-indicator__hint">We could not read your sign-in state.</p>
      </template>
      <template v-else-if="isExpired">
        <p class="session-expiry-indicator__title">Session ended</p>
        <p class="session-expiry-indicator__hint">Sign in again to continue.</p>
      </template>
      <template v-else>
        <p class="session-expiry-indicator__eyebrow">Time remaining</p>
        <p class="session-expiry-indicator__countdown">{{ formattedRemaining }}</p>
        <p class="session-expiry-indicator__hint">
          Log out and log in again for a new session.
        </p>
      </template>
    </div>
    <button
      v-if="hasToken"
      type="button"
      class="session-expiry-indicator__logout"
      @click="onLogoutClick"
    >
      <i class="bi bi-box-arrow-right" aria-hidden="true"></i>
      Log out
    </button>
  </div>
</template>

<script>
/**
 * Shows time remaining until JWT expires (reads `exp` from token in localStorage).
 * Matches server signing options (e.g. JWT_EXPIRES_IN=24h) — no extra API call.
 */

import {
  getSessionAlertMinutesBeforeExpiry,
  SESSION_ALERT_CHANGED_EVENT,
  SESSION_ALERT_STORAGE_KEY
} from '@/utils/sessionAlertPreference.service';
import { formatJwtExpiryForDisplay } from '@/utils/sessionExpiryDisplay';
import {
  getSessionTimerBarVisible,
  SESSION_TIMER_BAR_VISIBLE_EVENT
} from '@/utils/sessionTimerBarVisibility.service';

function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = parts[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getTokenExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || payload.exp == null) return null;
  const sec = Number(payload.exp);
  if (!Number.isFinite(sec)) return null;
  return sec * 1000;
}

export default {
  name: 'SessionExpiryIndicator',
  emits: ['logout', 'session-expired', 'session-nearing-expiry'],
  data() {
    return {
      nowTick: Date.now(),
      expiresAtMs: null,
      tokenError: false,
      pollId: null,
      expiryEmitted: false,
      alertPrefsTick: 0,
      /** One emit per "entry" into the configured alert window; reset when leaving or token changes. */
      nearingZoneEmittedForEntry: false,
      timerBarTick: 0
    };
  },
  computed: {
    hasToken() {
      return !!(this.$store.state.auth?.token || localStorage.getItem('token'));
    },
    remainingMs() {
      if (!this.expiresAtMs) return 0;
      return Math.max(0, this.expiresAtMs - this.nowTick);
    },
    isExpired() {
      return !!(this.expiresAtMs && this.remainingMs <= 0);
    },
    showBlock() {
      return this.hasToken && (this.tokenError || this.expiresAtMs);
    },
    timerBarVisible() {
      void this.timerBarTick;
      return getSessionTimerBarVisible();
    },
    /** Minutes-before-expiry threshold from Settings → Session Alert (default 1). */
    alertBeforeMs() {
      void this.nowTick;
      void this.alertPrefsTick;
      return getSessionAlertMinutesBeforeExpiry() * 60 * 1000;
    },
    /** Amber: from user threshold down to (but not including) the final minute. */
    isWarning() {
      const lastMinuteMs = 60 * 1000;
      return (
        !this.isExpired &&
        this.remainingMs > lastMinuteMs &&
        this.remainingMs <= this.alertBeforeMs
      );
    },
    /** Red: final minute before expiry (always). */
    isCritical() {
      return !this.isExpired && this.remainingMs > 0 && this.remainingMs <= 60 * 1000;
    },
    /** True when remaining time is inside the user's Session Alert window (amber/red banner), before expiry. */
    isWithinConfiguredAlert() {
      return (
        !this.isExpired &&
        this.remainingMs > 0 &&
        this.remainingMs <= this.alertBeforeMs
      );
    },
    formattedRemaining() {
      const ms = this.remainingMs;
      if (ms <= 0) return '0s';
      const totalSec = Math.floor(ms / 1000);
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      const s = totalSec % 60;
      if (h > 0) {
        return `${h}h ${m}m ${s}s`;
      }
      if (m > 0) {
        return `${m}m ${s}s`;
      }
      return `${s}s`;
    },
    tooltipText() {
      if (this.tokenError) return 'Invalid session token';
      if (!this.expiresAtMs) return '';
      if (this.isExpired) return 'Your session has expired';
      return `Session expires at ${formatJwtExpiryForDisplay(this.expiresAtMs)}`;
    }
  },
  watch: {
    isExpired: {
      immediate: true,
      handler(val) {
        if (val && !this.expiryEmitted) {
          this.expiryEmitted = true;
          this.$emit('session-expired');
        }
      }
    }
  },
  mounted() {
    this.syncFromToken();
    this.pollId = window.setInterval(() => {
      this.nowTick = Date.now();
      this.syncFromToken();
    }, 1000);
    window.addEventListener('storage', this.onStorage);
    window.addEventListener(SESSION_ALERT_CHANGED_EVENT, this.bumpAlertPrefs);
    window.addEventListener(SESSION_TIMER_BAR_VISIBLE_EVENT, this.bumpTimerBarVisible);
  },
  beforeUnmount() {
    if (this.pollId) {
      clearInterval(this.pollId);
      this.pollId = null;
    }
    window.removeEventListener('storage', this.onStorage);
    window.removeEventListener(SESSION_ALERT_CHANGED_EVENT, this.bumpAlertPrefs);
    window.removeEventListener(SESSION_TIMER_BAR_VISIBLE_EVENT, this.bumpTimerBarVisible);
  },
  methods: {
    bumpTimerBarVisible() {
      this.timerBarTick += 1;
    },
    bumpAlertPrefs() {
      this.alertPrefsTick += 1;
      this.$nextTick(() => this.checkNearingExpiryEdge());
    },
    onStorage(e) {
      /* key must match SESSION_TIMER_BAR_VISIBLE_KEY in sessionTimerBarVisibility.service.js */
      if (e.key === 'sessionTimerBarVisible' || e.key === null) {
        this.bumpTimerBarVisible();
      }
      if (e.key === SESSION_ALERT_STORAGE_KEY || e.key === null) {
        this.bumpAlertPrefs();
      }
      if (e.key === 'token' || e.key === null) {
        this.expiryEmitted = false;
        this.nearingZoneEmittedForEntry = false;
        this.syncFromToken();
      }
    },
    syncFromToken() {
      const token = this.$store.state.auth?.token || localStorage.getItem('token');
      if (!token) {
        this.expiresAtMs = null;
        this.tokenError = false;
        this.expiryEmitted = false;
        this.nearingZoneEmittedForEntry = false;
        return;
      }
      const expMs = getTokenExpiryMs(token);
      if (expMs == null) {
        this.expiresAtMs = null;
        this.tokenError = true;
        this.nearingZoneEmittedForEntry = false;
        return;
      }
      this.tokenError = false;
      if (this.expiresAtMs !== expMs) {
        this.expiresAtMs = expMs;
        this.expiryEmitted = false;
        this.nearingZoneEmittedForEntry = false;
      }
      this.checkNearingExpiryEdge();
    },
    checkNearingExpiryEdge() {
      if (!this.expiresAtMs || this.tokenError) {
        this.nearingZoneEmittedForEntry = false;
        return;
      }
      if (this.isExpired) {
        this.nearingZoneEmittedForEntry = false;
        return;
      }
      const inside = this.isWithinConfiguredAlert;
      if (!inside) {
        this.nearingZoneEmittedForEntry = false;
        return;
      }
      if (!this.nearingZoneEmittedForEntry) {
        this.nearingZoneEmittedForEntry = true;
        this.$emit('session-nearing-expiry', {
          expiresAtMs: this.expiresAtMs,
          formattedRemaining: this.formattedRemaining
        });
      }
    },
    onLogoutClick() {
      this.$emit('logout');
    }
  }
};
</script>

<style scoped src="./SessionExpiryIndicator.css"></style>
